import { useState } from 'react';
import styled from 'styled-components';

import { ModalBody, ModalFooter } from 'ui/modal';
import { spacing } from 'ui/theme';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';

import { FormFieldKey, FormState } from './types';
import { OverwriteWarning } from './OverwriteWarning';
import { StartImportButton } from './StartImportButton';
import { ImportDetails } from './ImportDetails';
import { ConfirmImportInput } from './ConfirmImportInput';
import { getAffectedIdsFromSettings } from './utils/getAffectedIdsFromSettings';
import { GO_BACK } from './constants';
import { useFeatureContext } from './FeatureContext';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.base};
`;

type ReviewChangesStepProps = Pick<
  FormState,
  FormFieldKey.IdType | FormFieldKey.ImportType | FormFieldKey.ChangeComment
> & {
  onGoBack: () => void;
  onSuccessfulImport: () => void;
  env: string;
};

export const ReviewChangesStep = ({
  idType,
  importType,
  changeComment,
  onGoBack,
  env,
  onSuccessfulImport,
}: ReviewChangesStepProps) => {
  const [isValid, setIsValid] = useState(false);
  const { featureFlag } = useFeatureContext();

  const affectedIds = getAffectedIdsFromSettings(featureFlag.settings, idType, importType);
  const shouldShowOverwriteWarning = affectedIds.length > 0;

  return (
    <>
      <ModalBody>
        <Layout data-test="review-changes-step">
          <ImportDetails idType={idType} importType={importType} />
          {shouldShowOverwriteWarning && (
            <OverwriteWarning
              affectedIdCount={affectedIds.length}
              idType={idType}
              importType={importType}
            />
          )}
          <Banner variant={BannerVariants.NEUTRAL} title="Change comment">
            {`"${changeComment}"`}
          </Banner>
          <ConfirmImportInput env={env} onChange={setIsValid} />
        </Layout>
      </ModalBody>
      <ModalFooter onClose={onGoBack} cancelLabel={GO_BACK}>
        <StartImportButton
          idType={idType}
          importType={importType}
          changeComment={changeComment}
          isDisabled={!isValid}
          onSuccessfulImport={onSuccessfulImport}
        />
      </ModalFooter>
    </>
  );
};
